import moment from 'moment-timezone';
import { createSelector } from 'reselect';
import { get, isEmpty, zip } from 'lodash';
import { metricKeys } from '@pulse/metrics';
import {
  METRICS_MAPPING_ENGAGEMENT,
  METRICS_MAPPING_DURATION,
} from '@utils/metrics/engagementMetrics';
import { getRangeBuckets } from '@utils/metrics/engagementBucket';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import {
  filterSelector,
  intervalSelector,
  realTimeModeOnTrendsSelector,
} from '@store/common/selectors';
import { getMetricDisplayName } from '@src/utils/metrics';
import { lineChartReqBody as diagReqBody } from '@components/metrics-panel-hz/utils/metricCardsReqBodyTemplate';
import {
  IInterval,
  chartIntervalSelector,
  engagementTypeSelector,
  bucketSelector,
} from '../selectors';
import { getBuckets, getBucketsData } from '../../utils/distribution';
import { ENGAGEMENT_TYPE } from '../reducer';
import * as types from './types';

const REST_BUCKET = 'REST';

export const engagementSelector = (state) => get(state, types.SCOPE);

export const engagementMetricSelector: any = createSelector(
  selectedMetricSelector,
  engagementTypeSelector,
  (metric: keyof typeof metricKeys, engagementType) =>
    engagementType === ENGAGEMENT_TYPE.endPlays
      ? METRICS_MAPPING_ENGAGEMENT[metric]
      : METRICS_MAPPING_DURATION[metric],
);

export const engagementDataSelector = createSelector(engagementSelector, (store) =>
  get(store, ['response', 'data', 0, 'result'], null),
);

export const barChartXAxisDataSelector: any = createSelector(
  engagementDataSelector,
  engagementMetricSelector,
  getBuckets,
);

export const engagementBarDataSelector: any = createSelector(
  barChartXAxisDataSelector,
  engagementDataSelector,
  engagementMetricSelector,
  getBucketsData,
);

export const isFetchingSelector = createSelector(engagementSelector, (engagementStore) =>
  get(engagementStore, 'isFetching'),
);

export const isErrorSelector = createSelector(engagementSelector, (engagementStore) =>
  get(engagementStore, 'errorCode'),
);

export const querySelector = createSelector(
  filterSelector,
  intervalSelector,
  chartIntervalSelector,
  engagementMetricSelector,
  bucketSelector,
  selectedMetricSelector,
  realTimeModeOnTrendsSelector,
  (
    filter,
    interval: IInterval,
    chartInterval: IInterval,
    engagementMetric,
    bucket,
    selectedMetric,
    realTimeOnTrends,
  ) => {
    if (!engagementMetric) {
      return null;
    }
    const from = chartInterval.from || interval.from;
    const to = chartInterval.to || interval.to;
    const body = JSON.parse(JSON.stringify(diagReqBody));

    body.queries[0].filter = filter;
    body.queries[0].metrics = [engagementMetric.metric];
    body.queries[0].dimension = engagementMetric.dimension;
    body.queries[0].interval = `${moment(from).toISOString()}/${moment(to).toISOString()}`;
    body.queries[0].isLiveMode = realTimeOnTrends;
    body.queries[0].queryGranularity = 'ALL';
    if (bucket) {
      body.queries[0].bucketizer = {
        type: 'range',
        buckets: getRangeBuckets(bucket, body.queries[0].dimension),
        defaultBucket: REST_BUCKET,
      };
    }

    delete body.queries[0].granularity;
    delete body.queries[0].type;
    delete body.queries[0].options;

    if (selectedMetric === metricKeys.PercentageComplete) {
      body.queries[0].options = {
        inPercentages: true,
      };
    }
    return body.queries[0];
  },
);

export const exportDataSelector = createSelector(
  engagementMetricSelector,
  barChartXAxisDataSelector,
  engagementBarDataSelector,
  (engagementMetric: any, barChartXAxis, barData) => {
    if (engagementMetric && !isEmpty(barData)) {
      const { xAxisName, yAxisName, metric } = engagementMetric;
      const metricDisplayName = getMetricDisplayName({ metricKey: metric });
      return {
        data: zip(barChartXAxis, barData),
        headers: [
          {
            title: xAxisName,
            key: xAxisName,
          },
          {
            title: yAxisName,
            key: yAxisName,
          },
        ],
        widgetInfo: {
          title: `${metricDisplayName} Distrbution`,
        },
      };
    }

    return { data: [], headers: [], widgetInfo: {} };
  },
);
